/**
 * "Have I already told the operator about this?", persisted.
 *
 * The notification watcher polls every 30 seconds (D11) and every poll
 * returns the SAME outstanding work until somebody deals with it: an
 * approval sits pending for an hour, an unread message stays unread over
 * a weekend. Without a memory of what has already been announced, that is
 * one OS notification every 30 seconds for the same thing — which is
 * precisely the burden D11 exists to rule out.
 *
 * The memory has to survive a reload, because a reload is the single most
 * common thing an operator does to a console and it must not re-announce
 * yesterday's queue. So: `localStorage`, under one key.
 *
 * And it has to be BOUNDED. A key per event, in a tab left open for
 * months against a busy tenant, is unbounded growth in a 5 MB store that
 * the rest of the app also uses (the theme preference lives there). The
 * cap evicts the OLDEST keys, which is the right end to drop: an event
 * announced 500 events ago is one nobody is still looking at, and the
 * worst case from evicting it — it reappears and is announced once more —
 * is a single duplicate rather than a lost notification.
 *
 * This module is deliberately storage-aware but framework-free, so the
 * eviction and reload behaviour can be tested without rendering anything.
 */

/** Keys kept before the oldest are dropped. D11 names this number. */
export const DEDUPE_CAP = 500

/** One key for the whole watcher. Prefixed like every other key this app
 * writes, so a shared origin stays legible in devtools. */
export const DEDUPE_STORAGE_KEY = 'stoaria.notified'

/**
 * `localStorage`, or `undefined` where there is none.
 *
 * The DOM lib types `globalThis.localStorage` as always present, which
 * is false in three environments this bundle runs in: SSR on a Worker,
 * jsdom under some configurations, and a profile where site data is
 * blocked (there the property access itself can throw). The widening
 * cast tells the type system the truth rather than hiding a bug — and
 * it is a widening, so nothing downstream is asserted into existence.
 */
function storage(): Storage | undefined {
  return (globalThis as { localStorage?: Storage }).localStorage
}

/**
 * Reads the persisted key list.
 *
 * Returns `[]` for every failure mode there is — no `localStorage` (SSR,
 * a Worker render, a profile with storage disabled), a quota error on
 * read, a value that is not JSON, a JSON value that is not an array of
 * strings. Nothing is swallowed that a caller could act on: the only
 * consequence of a failed read is that the watcher announces each
 * outstanding item once more, which is the behaviour of a first visit.
 */
function readKeys(storageKey: string): string[] {
  try {
    const raw = storage()?.getItem(storageKey)
    if (raw === null || raw === undefined) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((entry): entry is string => typeof entry === 'string')
  } catch {
    return []
  }
}

/**
 * A bounded, insertion-ordered set of "already announced" keys, backed by
 * `localStorage`.
 *
 * Insertion order is what makes "evict the oldest" meaningful, and a
 * `Set` gives it for free: re-adding an existing key does NOT move it to
 * the end, so a long-pending approval that is seen on every poll still
 * ages out normally rather than pinning itself at the head of the list
 * forever.
 */
export class SeenKeyRing {
  private readonly keys: Set<string>

  private constructor(
    private readonly storageKey: string,
    private readonly cap: number,
    initial: readonly string[],
  ) {
    // Trim on load as well as on add: a ring persisted by an older build
    // with a larger cap, or a hand-edited value, must not be honoured.
    this.keys = new Set(initial.slice(-cap))
  }

  /** Loads what this browser remembers. Cheap enough to call on every
   * mount; the watcher does exactly that. */
  static load(
    storageKey: string = DEDUPE_STORAGE_KEY,
    cap: number = DEDUPE_CAP,
  ): SeenKeyRing {
    return new SeenKeyRing(storageKey, cap, readKeys(storageKey))
  }

  has(key: string): boolean {
    return this.keys.has(key)
  }

  /** Records a key, evicting the oldest if that put us over the cap.
   *
   * Eviction happens HERE rather than only at save time so the in-memory
   * set of a tab that is never reloaded is bounded too — that tab is the
   * one that runs for a month. */
  add(key: string): void {
    if (this.keys.has(key)) return
    this.keys.add(key)
    while (this.keys.size > this.cap) {
      // `Set` iteration is insertion order, so the first entry is the
      // oldest. `next().value` is `string | undefined` under
      // `noUncheckedIndexedAccess`-style strictness even though the
      // loop condition proves it is present, so it is checked rather
      // than asserted.
      const oldest = this.keys.values().next().value
      if (oldest === undefined) return
      this.keys.delete(oldest)
    }
  }

  /** The keys, oldest first. For tests and for debugging; the watcher
   * only ever asks `has`. */
  snapshot(): string[] {
    return [...this.keys]
  }

  /**
   * Writes the ring back.
   *
   * A failed write is not reported anywhere, and that is the right call
   * for this one thing: the alternative is an error banner on a console
   * because a private window would not persist a dedupe list. The cost
   * of the failure is bounded and self-correcting — the next reload
   * re-announces what is still outstanding, once.
   */
  persist(): void {
    try {
      storage()?.setItem(this.storageKey, JSON.stringify([...this.keys]))
    } catch {
      // Private mode, a full quota, or storage disabled by policy.
    }
  }
}
